import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { validateResetToken, resetPassword } from '../utils/api';

const ResetPassword = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const data = await validateResetToken(token);
        if (data.valid) {
          setTokenValid(true);
          setEmail(data.email || '');
        } else {
          setErrorCode(data.code || 'INVALID_TOKEN');
        }
      } catch (err) {
        setErrorCode('INVALID_TOKEN');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 6) { setSubmitError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setSubmitError('Passwords do not match'); return; }
    setSubmitLoading(true);
    try {
      const data = await resetPassword(token, password);
      if (data.success) {
        setSuccess(true);
      } else {
        if (data.code === 'ALREADY_USED') setErrorCode('ALREADY_USED');
        else if (data.code === 'EXPIRED') setErrorCode('EXPIRED');
        else setSubmitError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to reset password');
    } finally {
      setSubmitLoading(false);
    }
  };

  const getErrorMessage = () => {
    switch (errorCode) {
      case 'ALREADY_USED': return 'This password reset link has already been used.';
      case 'EXPIRED': return 'This reset link has expired.';
      case 'INVALID_TOKEN': default: return 'Invalid or expired reset link.';
    }
  };

  const getErrorIcon = () => {
    switch (errorCode) {
      case 'ALREADY_USED': return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
      );
      case 'EXPIRED': return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
      );
      default: return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
      );
    }
  };

  return (
    <main className="main-content">
      <div className="reset-password-page">
        <div className="reset-password-card animate-drop">
          {loading ? (
            <div className="reset-loading">
              <span className="loading-spinner" />
              <p>Validating reset link...</p>
            </div>
          ) : success ? (
            <div className="reset-result">
              <div className="reset-icon success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
              </div>
              <h2>Password Reset Successfully!</h2>
              <p className="reset-subtitle">Your password has been updated. You can now log in with your new password.</p>
              <Link to="/" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', textAlign: 'center' }}>Go Home</Link>
            </div>
          ) : errorCode ? (
            <div className="reset-result">
              <div className="reset-icon error">{getErrorIcon()}</div>
              <h2>{errorCode === 'ALREADY_USED' ? 'Link Already Used' : errorCode === 'EXPIRED' ? 'Link Expired' : 'Invalid Link'}</h2>
              <p className="reset-subtitle">{getErrorMessage()}</p>
              <Link to="/" className="btn btn-secondary" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', textAlign: 'center' }}>Go Home</Link>
            </div>
          ) : tokenValid ? (
            <div className="reset-form-container">
              <div className="reset-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--green-primary)" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <h2>Reset Your Password</h2>
              <p className="reset-subtitle">Enter a new password for <strong>{email}</strong></p>
              {submitError && <div className="auth-error">{submitError}</div>}
              <form onSubmit={handleSubmit} className="reset-form">
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-input" placeholder="Enter new password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input type="password" className="form-input" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} minLength={6} required />
                </div>
                <button type="submit" className="btn btn-primary" disabled={submitLoading} style={{ width: '100%' }}>
                  {submitLoading ? <span className="loading-spinner" /> : 'Save New Password'}
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
};

export default ResetPassword;
